import React, { useEffect, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import OrderedList from '@tiptap/extension-ordered-list';
import Blockquote from '@tiptap/extension-blockquote';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import { createLowlight, common } from 'lowlight';
import Color from '@tiptap/extension-color';
import TextStyle from '@tiptap/extension-text-style';
import Highlight from '@tiptap/extension-highlight';
import { Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Tabs, Tab, CircularProgress, Alert } from '@mui/material';
import { supabase } from '../../lib/supabase';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import ImageIcon from '@mui/icons-material/Image';
import LinkIcon from '@mui/icons-material/Link';
import CodeIcon from '@mui/icons-material/Code';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import TableChartIcon from '@mui/icons-material/TableChart';
import FormatColorTextIcon from '@mui/icons-material/FormatColorText';
import HighlightIcon from '@mui/icons-material/Highlight';
import { marked } from 'marked';

const RichTextEditor = ({ value, onChange, disabled, placeholder, minHeight = 120 }) => {
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [markdownDialogOpen, setMarkdownDialogOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [markdownText, setMarkdownText] = useState('');
  const [markdownTab, setMarkdownTab] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadCount, setUploadCount] = useState(0);
  const MAX_UPLOADS_PER_SESSION = 10; // 세션당 최대 10개 파일

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      OrderedList,
      Blockquote,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Underline,
      Image.configure({
        inline: true,
        allowBase64: true,
        HTMLAttributes: {
          class: 'custom-image',
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'custom-link',
        },
      }),
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableCell,
      TableHeader,
      CodeBlockLowlight.configure({
        lowlight: createLowlight(common),
      }),
      TextStyle,
      Color,
      Highlight.configure({
        multicolor: true,
      }),
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none',
      },
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [disabled, editor]);

  useEffect(() => {
    if (editor && !editor.getText() && placeholder) {
      editor.commands.setContent(`<p class="is-editor-empty" data-placeholder="${placeholder}"></p>`);
    }
  }, [editor, placeholder]);

  const handleImageInsert = () => {
    if (imageUrl) {
      editor.chain().focus().setImage({ src: imageUrl }).run();
      setImageUrl('');
      setImageDialogOpen(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // 업로드 횟수 제한 확인
    if (uploadCount >= MAX_UPLOADS_PER_SESSION) {
      setUploadError(`세션당 최대 ${MAX_UPLOADS_PER_SESSION}개 파일만 업로드 가능합니다.`);
      return;
    }

    // 파일 유효성 검사 (허용된 이미지 타입만)
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type.toLowerCase())) {
      setUploadError('JPG, PNG, GIF, WebP 파일만 업로드 가능합니다.');
      return;
    }

    if (file.size > 2 * 1024 * 1024) { // 2MB 제한으로 강화
      setUploadError('파일 크기는 2MB 이하여야 합니다.');
      return;
    }

    setIsUploading(true);
    setUploadError('');

    try {
      // 버킷 존재 여부 확인을 생략하고 바로 업로드 시도
      // (일반 사용자는 listBuckets() 권한이 없을 수 있음)

      // 파일명 생성 (타임스탬프 + 안전한 파일명)
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop().toLowerCase();
      const safeName = file.name
        .replace(/[^a-zA-Z0-9.-]/g, '_') // 특수문자를 언더스코어로 변경
        .replace(/\s+/g, '_') // 공백을 언더스코어로 변경
        .replace(/_{2,}/g, '_'); // 연속된 언더스코어를 하나로 변경
      const fileName = `${timestamp}_${safeName}`;
      
      console.log('업로드할 파일명:', fileName);
      
      // Supabase Storage에 업로드
      const { data, error } = await supabase.storage
        .from('explanation-images')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error('Storage 업로드 상세 오류:', error);
        console.error('에러 코드:', error.statusCode);
        console.error('에러 세부사항:', error);
        
        if (error.message.includes('Bucket not found') || error.statusCode === 404) {
          throw new Error('이미지 저장소(explanation-images)를 찾을 수 없습니다. 관리자에게 문의하세요.');
        } else if (error.message.includes('invalid key')) {
          throw new Error('파일명에 사용할 수 없는 문자가 포함되어 있습니다.');
        } else if (error.message.includes('Duplicate')) {
          throw new Error('동일한 파일이 이미 존재합니다.');
        } else if (error.message.includes('Unauthorized') || error.statusCode === 401) {
          throw new Error('이미지 업로드 권한이 없습니다. 로그인 상태를 확인해주세요.');
        } else if (error.message.includes('Forbidden') || error.statusCode === 403) {
          throw new Error('이미지 업로드가 허용되지 않습니다. 관리자에게 문의하세요.');
        } else {
          throw new Error(`업로드 실패: ${error.message} (코드: ${error.statusCode || 'N/A'})`);
        }
      }

      // 공개 URL 생성
      const { data: { publicUrl } } = supabase.storage
        .from('explanation-images')
        .getPublicUrl(fileName);

      // 에디터에 이미지 삽입
      editor.chain().focus().setImage({ src: publicUrl }).run();
      
      // 업로드 횟수 증가
      setUploadCount(prev => prev + 1);
      
      // 상태 초기화
      setImageDialogOpen(false);
      event.target.value = ''; // 파일 입력 초기화

    } catch (error) {
      console.error('이미지 업로드 오류:', error);
      setUploadError(`업로드 실패: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleLinkInsert = () => {
    if (linkUrl) {
      if (linkText) {
        editor.chain().focus().insertContent(`<a href="${linkUrl}">${linkText}</a>`).run();
      } else {
        editor.chain().focus().setLink({ href: linkUrl }).run();
      }
      setLinkUrl('');
      setLinkText('');
      setLinkDialogOpen(false);
    }
  };

  const handleMarkdownOpen = () => {
    // 현재 HTML을 마크다운으로 변환 (테이블 지원 포함)
    const currentHtml = editor.getHTML();
    
    // 테이블을 마크다운으로 변환하는 함수
    const convertTablesToMarkdown = (html) => {
      return html.replace(/<table[^>]*>(.*?)<\/table>/gs, (match, tableContent) => {
        const rows = [];
        
        // 헤더 추출
        const headerMatch = tableContent.match(/<thead[^>]*>(.*?)<\/thead>/s);
        if (headerMatch) {
          const headerCells = headerMatch[1].match(/<th[^>]*>(.*?)<\/th>/g);
          if (headerCells) {
            const headerRow = headerCells.map(cell => 
              cell.replace(/<th[^>]*>(.*?)<\/th>/s, '$1').trim()
            ).join(' | ');
            rows.push(`| ${headerRow} |`);
            rows.push(`| ${headerCells.map(() => '---').join(' | ')} |`);
          }
        }
        
        // 바디 추출
        const bodyMatch = tableContent.match(/<tbody[^>]*>(.*?)<\/tbody>/s);
        let bodyContent = bodyMatch ? bodyMatch[1] : tableContent;
        
        // tr 태그로 행 분리
        const rowMatches = bodyContent.match(/<tr[^>]*>(.*?)<\/tr>/gs);
        if (rowMatches) {
          rowMatches.forEach(rowHtml => {
            const cellMatches = rowHtml.match(/<td[^>]*>(.*?)<\/td>/gs);
            if (cellMatches) {
              const rowData = cellMatches.map(cell => 
                cell.replace(/<td[^>]*>(.*?)<\/td>/s, '$1').trim()
              ).join(' | ');
              rows.push(`| ${rowData} |`);
            }
          });
        }
        
        return rows.join('\n');
      });
    };
    
    let simpleMarkdown = convertTablesToMarkdown(currentHtml);
    
    simpleMarkdown = simpleMarkdown
      .replace(/<img[^>]*src="([^"]*)"[^>]*>/g, '![]($1)')
      .replace(/<strong>(.*?)<\/strong>/g, '**$1**')
      .replace(/<em>(.*?)<\/em>/g, '*$1*')
      .replace(/<a[^>]*href="([^"]*)"[^>]*>(.*?)<\/a>/g, '[$2]($1)')
      .replace(/<h3>(.*?)<\/h3>/g, '### $1')
      .replace(/<h2>(.*?)<\/h2>/g, '## $1')
      .replace(/<h1>(.*?)<\/h1>/g, '# $1')
      .replace(/<p>(.*?)<\/p>/g, '$1\n\n')
      .replace(/<li>(.*?)<\/li>/g, '- $1')
      .replace(/<ul>|<\/ul>/g, '')
      .replace(/<ol>|<\/ol>/g, '')
      .replace(/\n\n+/g, '\n\n')
      .trim();
    
    setMarkdownText(simpleMarkdown);
    setMarkdownDialogOpen(true);
  };

  const handleMarkdownInsert = () => {
    try {
      // 마크다운을 HTML로 변환 (테이블 지원 포함)
      const htmlContent = marked(markdownText, {
        breaks: true,
        gfm: true,
        tables: true,
        sanitize: false
      });
      editor.commands.setContent(htmlContent);
      onChange(htmlContent);
      setMarkdownDialogOpen(false);
    } catch (error) {
      console.error('마크다운 변환 오류:', error);
    }
  };

  const deleteImageFromStorage = async (imageUrl) => {
    try {
      // explanation-images 버킷의 이미지인지 확인
      if (imageUrl.includes('/storage/v1/object/public/explanation-images/')) {
        const fileName = imageUrl.split('/explanation-images/')[1];
        
        console.log('Storage에서 이미지 삭제 시도:', fileName);
        
        const { error } = await supabase.storage
          .from('explanation-images')
          .remove([fileName]);
        
        if (error) {
          console.error('Storage 삭제 오류:', error);
        } else {
          console.log('Storage에서 이미지 삭제 완료:', fileName);
        }
      }
    } catch (error) {
      console.error('이미지 삭제 중 오류:', error);
    }
  };

  const handleDeleteSelectedContent = () => {
    // 선택된 내용에서 이미지 URL을 추출하여 Storage에서도 삭제
    const extractImageUrls = () => {
      const urls = [];
      const { from, to } = editor.state.selection;
      
      editor.state.doc.nodesBetween(from, to, (node) => {
        if (node.type.name === 'image' && node.attrs.src) {
          urls.push(node.attrs.src);
        }
      });
      
      return urls;
    };

    if (editor.state.selection.empty) {
      // 선택된 내용이 없으면 현재 커서 위치의 노드를 확인
      const { $from } = editor.state.selection;
      const node = $from.node();
      
      // 현재 위치가 이미지 노드인지 확인
      if (node && node.type.name === 'image') {
        if (node.attrs.src) {
          deleteImageFromStorage(node.attrs.src);
        }
        editor.commands.deleteSelection();
        return;
      }
      
      // 이미지를 찾기 위해 주변 노드들을 확인
      const pos = $from.pos;
      const resolvedPos = editor.state.doc.resolve(pos);
      
      // 이전 노드가 이미지인지 확인
      if (resolvedPos.nodeBefore && resolvedPos.nodeBefore.type.name === 'image') {
        if (resolvedPos.nodeBefore.attrs.src) {
          deleteImageFromStorage(resolvedPos.nodeBefore.attrs.src);
        }
        editor.commands.setTextSelection(pos - resolvedPos.nodeBefore.nodeSize, pos);
        editor.commands.deleteSelection();
        return;
      }
      
      // 다음 노드가 이미지인지 확인
      if (resolvedPos.nodeAfter && resolvedPos.nodeAfter.type.name === 'image') {
        if (resolvedPos.nodeAfter.attrs.src) {
          deleteImageFromStorage(resolvedPos.nodeAfter.attrs.src);
        }
        editor.commands.setTextSelection(pos, pos + resolvedPos.nodeAfter.nodeSize);
        editor.commands.deleteSelection();
        return;
      }
    } else {
      // 선택된 내용에 이미지가 있으면 Storage에서도 삭제
      const imageUrls = extractImageUrls();
      imageUrls.forEach(url => deleteImageFromStorage(url));
      
      // 선택된 내용 삭제
      editor.commands.deleteSelection();
    }
  };

  if (!editor) {
    return null;
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      border: '1px solid #e0e3e7',
      borderRadius: 1,
      overflow: 'hidden',
    }}>
      <Box sx={{ 
        display: 'flex', 
        flexWrap: 'wrap',
        gap: 0.5, 
        p: 0.5,
        borderBottom: '1px solid #e0e3e7',
        background: '#f8f9fa',
        flexShrink: 0,
      }}>
        <Button size="small" variant={editor.isActive('heading', { level: 1 }) ? 'contained' : 'text'} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} sx={{ minWidth: 40, px: 1 }} disabled={disabled}>H1</Button>
        <Button size="small" variant={editor.isActive('heading', { level: 2 }) ? 'contained' : 'text'} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} sx={{ minWidth: 40, px: 1 }} disabled={disabled}>H2</Button>
        <Button size="small" variant={editor.isActive('heading', { level: 3 }) ? 'contained' : 'text'} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} sx={{ minWidth: 40, px: 1 }} disabled={disabled}>H3</Button>
        <Button size="small" variant={editor.isActive('bold') ? 'contained' : 'text'} onClick={() => editor.chain().focus().toggleBold().run()} sx={{ minWidth: 40, px: 1, fontWeight: 'bold' }} disabled={disabled}>B</Button>
        <Button size="small" variant={editor.isActive('italic') ? 'contained' : 'text'} onClick={() => editor.chain().focus().toggleItalic().run()} sx={{ minWidth: 40, px: 1, fontStyle: 'italic' }} disabled={disabled}>I</Button>
        <Button size="small" variant={editor.isActive('underline') ? 'contained' : 'text'} onClick={() => editor.chain().focus().toggleUnderline().run()} sx={{ minWidth: 40, px: 1, textDecoration: 'underline' }} disabled={disabled}>U</Button>
        <Button size="small" variant={editor.isActive('highlight') ? 'contained' : 'text'} onClick={() => editor.chain().focus().toggleHighlight().run()} sx={{ minWidth: 40, px: 1 }} disabled={disabled} title="형광펜"><HighlightIcon fontSize="small" /></Button>
        <Button size="small" variant={editor.isActive('codeBlock') ? 'contained' : 'text'} onClick={() => editor.chain().focus().toggleCodeBlock().run()} sx={{ minWidth: 40, px: 1 }} disabled={disabled} title="코드 블록">{ }</Button>
        <Button size="small" variant={editor.isActive('bulletList') ? 'contained' : 'text'} onClick={() => editor.chain().focus().toggleBulletList().run()} sx={{ minWidth: 40, px: 1 }} disabled={disabled}>•</Button>
        <Button size="small" variant={editor.isActive('orderedList') ? 'contained' : 'text'} onClick={() => editor.chain().focus().toggleOrderedList().run()} sx={{ minWidth: 40, px: 1 }} disabled={disabled}>1.</Button>
        <Button size="small" variant={editor.isActive('blockquote') ? 'contained' : 'text'} onClick={() => editor.chain().focus().toggleBlockquote().run()} sx={{ minWidth: 40, px: 1 }} disabled={disabled}>"</Button>
        <Button size="small" variant={editor.isActive({ textAlign: 'left' }) ? 'contained' : 'text'} onClick={() => editor.chain().focus().setTextAlign('left').run()} sx={{ minWidth: 40, px: 1 }} disabled={disabled}><FormatAlignLeftIcon fontSize="small" /></Button>
        <Button size="small" variant={editor.isActive({ textAlign: 'center' }) ? 'contained' : 'text'} onClick={() => editor.chain().focus().setTextAlign('center').run()} sx={{ minWidth: 40, px: 1 }} disabled={disabled}><FormatAlignCenterIcon fontSize="small" /></Button>
        <Button size="small" variant={editor.isActive({ textAlign: 'right' }) ? 'contained' : 'text'} onClick={() => editor.chain().focus().setTextAlign('right').run()} sx={{ minWidth: 40, px: 1 }} disabled={disabled}><FormatAlignRightIcon fontSize="small" /></Button>
        <Button size="small" variant="text" onClick={() => setImageDialogOpen(true)} sx={{ minWidth: 40, px: 1 }} disabled={disabled}><ImageIcon fontSize="small" /></Button>
        <Button size="small" variant={editor.isActive('link') ? 'contained' : 'text'} onClick={() => setLinkDialogOpen(true)} sx={{ minWidth: 40, px: 1 }} disabled={disabled}><LinkIcon fontSize="small" /></Button>
        <Button size="small" variant={editor.isActive('table') ? 'contained' : 'text'} onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} sx={{ minWidth: 40, px: 1 }} disabled={disabled} title="테이블 삽입"><TableChartIcon fontSize="small" /></Button>
        <Button size="small" variant="text" onClick={handleDeleteSelectedContent} sx={{ minWidth: 40, px: 1 }} disabled={disabled} title="선택된 내용 삭제"><DeleteIcon fontSize="small" /></Button>
        <Button size="small" variant="text" onClick={handleMarkdownOpen} sx={{ minWidth: 40, px: 1 }} disabled={disabled}><CodeIcon fontSize="small" /></Button>
      </Box>
      <Box sx={{ 
        flex: 1, 
        overflow: 'auto',
        p: 2,
        background: '#fff',
        '& .ProseMirror': {
          minHeight: minHeight,
          height: '100%',
          outline: 'none',
          '& > * + *': {
            marginTop: '0.75em',
          },
          '& p.is-editor-empty:first-child::before': {
            content: 'attr(data-placeholder)',
            float: 'left',
            color: '#adb5bd',
            pointerEvents: 'none',
            height: 0,
          },
          '& .custom-image': {
            maxWidth: '100%',
            height: 'auto',
          },
          '& table': {
            width: '100%',
            borderCollapse: 'collapse',
            margin: '16px 0',
            border: '1px solid #ddd',
          },
          '& th, & td': {
            border: '1px solid #ddd',
            padding: '8px 12px',
            textAlign: 'left',
          },
          '& th': {
            backgroundColor: '#f5f5f5',
            fontWeight: 'bold',
          },
          '& tbody tr:nth-of-type(even)': {
            backgroundColor: '#f9f9f9',
          },
          '& pre': {
            background: '#f5f5f5',
            border: '1px solid #ddd',
            borderRadius: '4px',
            padding: '12px',
            fontSize: '14px',
            fontFamily: 'monospace',
            overflow: 'auto',
            lineHeight: 1.4,
          },
          '& code': {
            background: '#f0f0f0',
            padding: '2px 4px',
            borderRadius: '3px',
            fontSize: '14px',
            fontFamily: 'monospace',
          },
          '& mark': {
            backgroundColor: '#ffeb3b',
            padding: '1px 2px',
            borderRadius: '2px',
          },
          '& .custom-image': {
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            margin: '8px 0',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              transform: 'scale(1.02)',
            },
            '&.ProseMirror-selectednode': {
              outline: '3px solid #1976d2',
              outlineOffset: '2px',
            },
          },
          '& .custom-link': {
            color: '#1976d2',
            textDecoration: 'underline',
            '&:hover': {
              color: '#1565c0',
            },
          },
        },
      }}>
        <EditorContent editor={editor} />
      </Box>

      {/* 이미지 삽입 다이얼로그 */}
      <Dialog open={imageDialogOpen} onClose={() => setImageDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>이미지 삽입</DialogTitle>
        <DialogContent>
          {uploadError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {uploadError}
            </Alert>
          )}
          
          <Box sx={{ mb: 3 }}>
            <Box sx={{ mb: 1, fontWeight: 'bold' }}>파일 업로드</Box>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
              id="image-upload-input"
              disabled={isUploading}
            />
            <label htmlFor="image-upload-input">
              <Button
                variant="contained"
                component="span"
                disabled={isUploading}
                startIcon={isUploading ? <CircularProgress size={16} /> : null}
                sx={{ mb: 1 }}
              >
                {isUploading ? '업로드 중...' : '파일 선택'}
              </Button>
            </label>
            <Box sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
              지원 형식: JPG, PNG, GIF, WebP (최대 5MB)
            </Box>
          </Box>

          <Box sx={{ textAlign: 'center', mb: 2, color: 'text.secondary' }}>
            또는
          </Box>

          <TextField
            margin="dense"
            label="이미지 URL 직접 입력"
            fullWidth
            variant="outlined"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            placeholder="https://example.com/image.jpg"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setImageDialogOpen(false);
            setUploadError('');
            setImageUrl('');
          }}>취소</Button>
          <Button onClick={handleImageInsert} variant="contained" disabled={!imageUrl || isUploading}>
            삽입
          </Button>
        </DialogActions>
      </Dialog>

      {/* 링크 삽입 다이얼로그 */}
      <Dialog open={linkDialogOpen} onClose={() => setLinkDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>링크 삽입</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="링크 URL"
            fullWidth
            variant="outlined"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="https://example.com"
            sx={{ mt: 2 }}
          />
          <TextField
            margin="dense"
            label="링크 텍스트 (선택사항)"
            fullWidth
            variant="outlined"
            value={linkText}
            onChange={(e) => setLinkText(e.target.value)}
            placeholder="클릭하여 이동"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialogOpen(false)}>취소</Button>
          <Button onClick={handleLinkInsert} variant="contained" disabled={!linkUrl}>
            삽입
          </Button>
        </DialogActions>
      </Dialog>

      {/* 마크다운 편집 다이얼로그 */}
      <Dialog open={markdownDialogOpen} onClose={() => setMarkdownDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>마크다운 편집</DialogTitle>
        <DialogContent>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={markdownTab} onChange={(e, newValue) => setMarkdownTab(newValue)}>
              <Tab icon={<CodeIcon />} label="편집" />
              <Tab icon={<VisibilityIcon />} label="미리보기" />
            </Tabs>
          </Box>
          
          {markdownTab === 0 ? (
            <TextField
              multiline
              rows={12}
              fullWidth
              variant="outlined"
              value={markdownText}
              onChange={(e) => setMarkdownText(e.target.value)}
              placeholder="마크다운 문법을 사용하여 편집하세요...

예시:
**굵은 글씨**
*기울임체*
![이미지](URL)
[링크 텍스트](URL)
### 제목
- 목록 항목"
              sx={{ 
                '& .MuiInputBase-input': { 
                  fontFamily: 'monospace',
                  fontSize: '14px'
                }
              }}
            />
          ) : (
            <Box 
              sx={{ 
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                p: 2,
                minHeight: 300,
                maxHeight: 400,
                overflow: 'auto',
                backgroundColor: '#fafafa',
                '& img': {
                  maxWidth: '100%',
                  height: 'auto',
                  borderRadius: '4px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  margin: '8px 0',
                },
                '& a': {
                  color: '#1976d2',
                  textDecoration: 'underline',
                },
                '& table': {
                  width: '100%',
                  borderCollapse: 'collapse',
                  margin: '16px 0',
                  border: '1px solid #ddd',
                },
                '& th, & td': {
                  border: '1px solid #ddd',
                  padding: '8px 12px',
                  textAlign: 'left',
                },
                '& th': {
                  backgroundColor: '#f5f5f5',
                  fontWeight: 'bold',
                },
                '& tbody tr:nth-of-type(even)': {
                  backgroundColor: '#f9f9f9',
                },
              }}
              dangerouslySetInnerHTML={{ 
                __html: marked(markdownText, {
                  breaks: true,
                  gfm: true,
                  tables: true,
                  sanitize: false
                })
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarkdownDialogOpen(false)}>취소</Button>
          <Button onClick={handleMarkdownInsert} variant="contained">
            적용
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RichTextEditor; 